import { html } from 'lit';
import CardOption from '../card_option/element.js';
import translations from './translations.js';
import style from './style.lit.css';
import '../badge/element.js';

/**
 * One item in organize card
 *
 * @prop goal - Goal amount of helpers (optional)
 * @prop amountContributed - Amount of help the current user contributed in customAmount items
 * @prop shouldShowBadge - Should show badge with amountContributed?
 * @prop customAmount - Can amount of help offered be custom? (optional)
 *
 * @cssprop [--badge-color: var(--secondary)] Background color of amount-contributed badge
 *
 * @slot default - Title/label
 * @slot avatars - Avatar list of helpers
 */

export default class OrganizeOption extends CardOption {
  static styles = [CardOption.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    amountContributed: { type: Number },
    goal: { type: Number },
    customAmount: { type: Boolean },
    shouldShowBadge: { type: Boolean },
  };

  static config = {
    icons: {
      plus: 'plus-circle',
      minus: 'minus-circle',
      check: 'check',
    },
  };

  constructor() {
    super();
    this.amountContributed = 0;
  }

  /** @internal */
  get checkUrl() {
    return `/cards/${this.cardId}/items/${this.itemId}/assigns`;
  }

  /** @internal */
  get checkCustomUrl() {
    return `${this.checkUrl}/new`;
  }

  /** @internal */
  get uncheckUrl() {
    return `${this.checkUrl}/${this.cardId}`;
  }

  /** @internal */
  get detailsUrl() {
    return `/organize_cards/${this.cardId}/assigns?item=${this.itemId}`;
  }

  calcCheck(amount) {
    this.checked = true;
    this.users = [...this.users, this.currentUser];

    if (this.avatarList) {
      this.avatarList.users = this.users.slice(-this.maxAvatarsLength);
      this.avatarList.totalCount++;
    }

    this.amount += amount;
    this.amountContributed = amount;
  }

  check(amount = 1) {
    this.calcCheck(amount);
    this.dispatchChangeEvent();
    this.commonActions.displaySnackBarSuccess(this.t('snack.check_success'));
  }

  calcUncheck() {
    this.checked = false;
    this.users = this.users.filter(a => a.id !== this.currentUser.id);

    if (this.avatarList) {
      this.avatarList.users = this.users.slice(0, this.maxAvatarsLength);
      this.avatarList.totalCount--;
    }

    this.amount -= this.amountContributed;
    this.amountContributed = 0;
  }

  uncheck() {
    this.calcUncheck();
    this.dispatchChangeEvent();
    this.commonActions.displaySnackBarSuccess(this.t('snack.uncheck_success'));
  }

  async handleClick() {
    if (this.preventInput || this.disabled) return;
    this.preventInput = true;

    try {
      if (!this.checked && !this.customAmount) {
        this.check();
        await this.commonActions.post(this.checkUrl);
      } else if (this.checked) {
        this.uncheck();
        await this.commonActions.delete(this.uncheckUrl);
      } else await this.commonActions.execJSFromResponse(this.checkCustomUrl);
    } catch {
      this.checked ? this.uncheck() : this.check();
      this.commonActions.displaySnackBarError(
        this.t('snack.generic_error_msg')
      );
    } finally {
      this.preventInput = false;
    }
  }

  renderLabel() {
    return html`
      <ef-label>
        ${this.goal ? `${this.amount}/${this.goal}` : this.amount}
        <span class="count-label">
          ${this.t('count_label', {
            count: this.amount,
          })}
        </span>
      </ef-label>
    `;
  }

  renderAction() {
    return html`
      <div class="action-wrapper">
        ${this.shouldShowBadge ? this.renderBadge() : null}
        <button @click=${this.handleClick}>
          <ef-icon .name=${this.icon}></ef-icon>
        </button>
      </div>
    `;
  }

  renderCheckedIcon() {
    return html`
      <div class="action-wrapper">
        ${this.shouldShowBadge ? this.renderBadge() : null}
        <ef-icon name=${this.constructor.config.icons.check}></ef-icon>
      </div>
    `;
  }

  renderBadge() {
    const numberTooBig = this.amountContributed > 999;
    const count = numberTooBig ? '999+' : this.amountContributed;

    return html`
      <ef-badge .title=${this.amountContributed} .count=${count}></ef-badge>
    `;
  }
}

OrganizeOption.registerTagName('ef-organize-option');

customElements.get('ef-organize-option') ||
  customElements.define('ef-organize-option', OrganizeOption);
